import React from 'react'
import * as braintree from 'braintree-web-drop-in'
import {
  braintreePurchase,
  getNewBraintreeToken,
  pollBraintreePurchaseStatusUntilCompleted,
} from 'services/gamelogic'

import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import IconClose from '@material-ui/icons/Close'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContentText from '@material-ui/core/DialogContentText'
import Dialog, { DialogProps } from '@material-ui/core/Dialog'
import withMobileDialog from '@material-ui/core/withMobileDialog'
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles'

import T from 'components/FormattedMessage'
import Button from 'components/Button'
import StoreItemName from 'components/StoreItemName'

import { IStorePremiumProduct, IStoreProduct } from 'protos'

const styles = (theme: Theme) => createStyles({
  dialogTitle: {
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(),
    paddingRight: theme.spacing(),
  }
})

interface BraintreeInstance {
  requestPaymentMethod(): Promise<{ nonce: string }>
}

interface Props extends DialogProps {
  product: IStorePremiumProduct | IStoreProduct
  onSuccessPayment?: () => void
}

interface State {
  error: Error | null
  isLoading: boolean
  isButtonLoading: boolean
}

type InjectedProps = Props & WithStyles<typeof styles>

class CheckoutDialog extends React.Component<InjectedProps, State> {
  private braintreeContainerReference!: React.RefObject<HTMLDivElement>
  private braintreeInstance!: BraintreeInstance

  constructor(props: InjectedProps) {
    super(props)

    this.state = { error: null, isLoading: false, isButtonLoading: false }
    this.braintreeContainerReference = React.createRef()
  }

  async componentDidMount() {
    try {
      this.setState({ ...this.state, error: null, isLoading: true })

      const { token: authorization } = await getNewBraintreeToken()
      const { current: container } = this.braintreeContainerReference

      this.braintreeInstance = await braintree.create({ authorization, container })
    } catch (error) {
      this.setState({ ...this.state, error })
    } finally {
      this.setState({ ...this.state, isLoading: false })
    }
  }

  handleClickBuyButton = (productId: string) => async (e: React.SyntheticEvent) => {
    const { onSuccessPayment } = this.props

    try {
      this.setState({ ...this.state, error: null, isButtonLoading: true })

      const { nonce } = await this.braintreeInstance.requestPaymentMethod()
      const purchaseResponse = await braintreePurchase(nonce, productId)

      if (!purchaseResponse.complete) {
        await pollBraintreePurchaseStatusUntilCompleted(purchaseResponse.transactionId!)
      }

      if (onSuccessPayment) { onSuccessPayment() }
    } catch (error) {
      this.setState({ ...this.state, isButtonLoading: false, error })
    }
  }

  render() {
    const { product, classes: { dialogTitle }, onSuccessPayment, ...props } = this.props
    const { isLoading, isButtonLoading, error } = this.state
    const isButtonDisabled = isLoading || isButtonLoading || !!error

    // TODO: Translation keys needed
    return (
      <Dialog transitionDuration={0} disableBackdropClick {...props}>
        <DialogTitle className={dialogTitle}>
          <Grid alignItems="center" justify="space-between" container>
            <Grid zeroMinWidth item>
              <Typography variant="subtitle2">
                {isLoading && 'Loading...'}
                {!isLoading && <><span>Buy </span><StoreItemName product={product} /></>}
              </Typography>
            </Grid>
            <Grid zeroMinWidth item>
              <IconButton onClick={(e) => this.props.onClose?.(e, 'backdropClick')}>
                <IconClose fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText component="div">
            <div ref={this.braintreeContainerReference} />
            {!!error && <Typography color="error">{error.message}</Typography>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isButtonDisabled}
            onClick={this.handleClickBuyButton(product.productId!)}
            variant="contained"
            color="primary"
            fullWidth
          >
            <T id="autosub_action_buy" />
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

// TODO: https://next.material-ui.com/demos/dialogs/#responsive-full-screen
// tslint:disable-next-line: no-any
const CheckoutDialogStyled = withStyles(styles)(CheckoutDialog) as any

export default withMobileDialog<Props>()(CheckoutDialogStyled)
